/**
 * Step 6: Submit
 *
 * Shows upload progress during submission and results after completion.
 * Offers the option to save the current column mapping as a template.
 */

import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorIcon from '@mui/icons-material/Error';
import ReplayIcon from '@mui/icons-material/Replay';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation, Trans } from 'react-i18next';
import { UploadProgress } from '../utils/types';
import { ValidationStats } from '../utils/validate';

interface StepSubmitProps {
	progress: UploadProgress;
	onSaveTemplate: (name: string, description?: string) => Promise<void>;
	onRetryFailed: () => void;
	onExportFailed: () => void;
	stats: ValidationStats | null;
}

export const StepSubmit: React.FC<StepSubmitProps> = ({
	progress,
	onSaveTemplate,
	onRetryFailed,
	onExportFailed,
	stats,
}) => {
	const { t } = useTranslation();
	const [showSaveDialog, setShowSaveDialog] = useState(false);
	const [showAllErrors, setShowAllErrors] = useState(false);
	const [templateName, setTemplateName] = useState('');
	const [templateDesc, setTemplateDesc] = useState('');
	const [isSaving, setIsSaving] = useState(false);

	const handleSaveTemplate = async () => {
		if (!templateName.trim()) return;
		setIsSaving(true);
		try {
			await onSaveTemplate(templateName.trim(), templateDesc.trim() || undefined);
			setShowSaveDialog(false);
			setTemplateName('');
			setTemplateDesc('');
		} catch {
			// Error handled by parent
		} finally {
			setIsSaving(false);
		}
	};

	// Pre-submit state
	if (progress.status === 'idle') {
		return (
			<Box>
				<Alert severity="info" sx={{ mb: 3 }}>
					<Trans
						i18nKey="bulkUpload.submit.readyToSubmit"
						values={{
							validRows: stats?.valid ?? 0,
							warningNote:
								(stats?.warning ?? 0) > 0
									? t('bulkUpload.submit.warningNote', { count: stats?.warning })
									: '',
							errorNote:
								(stats?.error ?? 0) > 0
									? t('bulkUpload.submit.errorNote', { count: stats?.error })
									: '',
						}}
						components={{ strong: <strong /> }}
					/>
				</Alert>

				{/* Save template option */}
				<Button
					variant="outlined"
					startIcon={<SaveIcon />}
					onClick={() => setShowSaveDialog(true)}
					sx={{ mb: 2 }}>
					{t('bulkUpload.submit.saveColumnMapping')}
				</Button>

				<Typography variant="body2" color="text.secondary">
					<Trans i18nKey="bulkUpload.submit.clickSubmit" components={{ strong: <strong /> }} />
				</Typography>

				{/* Save template dialog */}
				<Dialog
					open={showSaveDialog}
					onClose={() => setShowSaveDialog(false)}
					maxWidth="sm"
					fullWidth>
					<DialogTitle>{t('bulkUpload.submit.saveUploadTemplate')}</DialogTitle>
					<DialogContent>
						<TextField
							autoFocus
							fullWidth
							label={t('bulkUpload.submit.templateName')}
							value={templateName}
							onChange={(e) => setTemplateName(e.target.value)}
							sx={{ mt: 1, mb: 2 }}
						/>
						<TextField
							fullWidth
							label={t('bulkUpload.submit.descriptionOptional')}
							value={templateDesc}
							onChange={(e) => setTemplateDesc(e.target.value)}
							multiline
							rows={2}
							sx={{ mb: 2 }}
						/>
						<Box sx={{ textAlign: 'right' }}>
							<Button onClick={() => setShowSaveDialog(false)} sx={{ mr: 1 }}>
								{t('cancel')}
							</Button>
							<Button
								variant="contained"
								onClick={handleSaveTemplate}
								disabled={!templateName.trim() || isSaving}>
								{isSaving ? t('bulkUpload.submit.saving') : t('bulkUpload.submit.saveTemplate')}
							</Button>
						</Box>
					</DialogContent>
				</Dialog>
			</Box>
		);
	}

	// Submitting state
	if (progress.status === 'submitting') {
		const pct =
			progress.totalRows > 0 ? Math.round((progress.processedRows / progress.totalRows) * 100) : 0;

		return (
			<Box>
				<Typography variant="h6" sx={{ mb: 2 }}>
					{t('bulkUpload.submit.uploading')}
				</Typography>

				<Box sx={{ mb: 2 }}>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
						<Typography variant="body2">
							{t('bulkUpload.submit.batch', {
								current: progress.currentBatch,
								total: progress.totalBatches,
							})}
						</Typography>
						<Typography variant="body2">{pct}%</Typography>
					</Box>
					<LinearProgress variant="determinate" value={pct} />
				</Box>

				<Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
					<Typography variant="body2">
						{t('bulkUpload.submit.rowProgress', {
							processed: progress.processedRows,
							total: progress.totalRows,
						})}
					</Typography>
					<Chip
						icon={<CheckCircleIcon />}
						label={t('bulkUpload.submit.ok', { count: progress.successfulRows })}
						color="success"
						size="small"
						variant="outlined"
					/>
					{progress.failedRows > 0 && (
						<Chip
							icon={<ErrorIcon />}
							label={t('bulkUpload.submit.failed', { count: progress.failedRows })}
							color="error"
							size="small"
							variant="outlined"
						/>
					)}
				</Box>
			</Box>
		);
	}

	// Complete or error state
	return (
		<Box>
			{progress.status === 'complete' && progress.failedRows === 0 && (
				<Alert severity="success" sx={{ mb: 3 }}>
					<Typography variant="subtitle1">{t('bulkUpload.submit.uploadComplete')}</Typography>
					<Typography variant="body2">
						{t('bulkUpload.submit.successfullyCreated', { count: progress.successfulRows })}
					</Typography>
				</Alert>
			)}

			{progress.status === 'complete' && progress.failedRows > 0 && (
				<Alert severity="warning" sx={{ mb: 3 }}>
					<Typography variant="subtitle1">
						{t('bulkUpload.submit.uploadCompleteWithErrors')}
					</Typography>
					<Typography variant="body2">
						{t('bulkUpload.submit.succeededFailed', {
							succeeded: progress.successfulRows,
							failed: progress.failedRows,
						})}
					</Typography>
				</Alert>
			)}

			{progress.status === 'error' && (
				<Alert severity="error" sx={{ mb: 3 }}>
					<Typography variant="subtitle1">{t('bulkUpload.submit.uploadFailed')}</Typography>
					<Typography variant="body2">{progress.error}</Typography>
				</Alert>
			)}

			{/* Results summary */}
			<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
				<Chip
					label={t('bulkUpload.submit.created', { count: progress.successfulRows })}
					color="success"
					variant="outlined"
				/>
				{progress.failedRows > 0 && (
					<Chip
						label={t('bulkUpload.submit.failed', { count: progress.failedRows })}
						color="error"
						variant="outlined"
					/>
				)}
				{progress.skippedRows > 0 && (
					<Chip
						label={t('bulkUpload.submit.skippedCount', { count: progress.skippedRows })}
						variant="outlined"
					/>
				)}
			</Box>

			{/* Failed row details */}
			{(() => {
				const failedResults = progress.results.filter((r) => r.status === 'error');
				const visibleResults = showAllErrors ? failedResults : failedResults.slice(0, 10);
				const hiddenCount = failedResults.length - 10;

				return (
					<>
						{visibleResults.map((r) => (
							<Alert key={r.rowIndex} severity="error" sx={{ mb: 1 }}>
								{t('bulkUpload.submit.rowError', {
									row: r.rowIndex + 1,
									errors: r.errors?.join(', '),
								})}
							</Alert>
						))}
						{hiddenCount > 0 && (
							<Button
								size="small"
								onClick={() => setShowAllErrors(!showAllErrors)}
								sx={{ mt: 0.5, mb: 1 }}>
								{showAllErrors
									? t('bulkUpload.submit.showLess', 'Show less')
									: t('bulkUpload.submit.moreErrors', {
											count: hiddenCount,
										})}
							</Button>
						)}
					</>
				);
			})()}

			{/* Retry / Export failed rows */}
			{progress.status === 'complete' && progress.failedRows > 0 && (
				<Box sx={{ display: 'flex', gap: 2, mt: 3, mb: 2 }}>
					<Button
						variant="contained"
						color="warning"
						startIcon={<ReplayIcon />}
						onClick={onRetryFailed}>
						{t('bulkUpload.submit.retryFailed', { count: progress.failedRows })}
					</Button>
					<Button variant="outlined" startIcon={<DownloadIcon />} onClick={onExportFailed}>
						{t('bulkUpload.submit.downloadFailedRows')}
					</Button>
				</Box>
			)}

			{/* Save template after successful upload */}
			{progress.status === 'complete' && progress.successfulRows > 0 && (
				<Box sx={{ mt: 3 }}>
					<Button
						variant="outlined"
						startIcon={<SaveIcon />}
						onClick={() => setShowSaveDialog(true)}>
						{t('bulkUpload.submit.saveColumnMapping')}
					</Button>

					<Dialog
						open={showSaveDialog}
						onClose={() => setShowSaveDialog(false)}
						maxWidth="sm"
						fullWidth>
						<DialogTitle>{t('bulkUpload.submit.saveUploadTemplate')}</DialogTitle>
						<DialogContent>
							<TextField
								autoFocus
								fullWidth
								label={t('bulkUpload.submit.templateName')}
								value={templateName}
								onChange={(e) => setTemplateName(e.target.value)}
								sx={{ mt: 1, mb: 2 }}
							/>
							<TextField
								fullWidth
								label={t('bulkUpload.submit.descriptionOptional')}
								value={templateDesc}
								onChange={(e) => setTemplateDesc(e.target.value)}
								multiline
								rows={2}
								sx={{ mb: 2 }}
							/>
							<Box sx={{ textAlign: 'right' }}>
								<Button onClick={() => setShowSaveDialog(false)} sx={{ mr: 1 }}>
									{t('cancel')}
								</Button>
								<Button
									variant="contained"
									onClick={handleSaveTemplate}
									disabled={!templateName.trim() || isSaving}>
									{isSaving ? t('bulkUpload.submit.saving') : t('bulkUpload.submit.saveTemplate')}
								</Button>
							</Box>
						</DialogContent>
					</Dialog>
				</Box>
			)}
		</Box>
	);
};
